// src/sections/LeadershipSection.tsx
import React, {useEffect, useState} from "react";
import styled from "styled-components";
import {SFText18W400 as Text18W400, SText24W700 as Text24W700, SText40W700 as Text40W700,} from "../Typography";
import {useSingleton} from "../../cms/factory";
import {AsyncReveal} from "../AsyncReveal";
import closeIcon from "imgs/close.svg";
import { ReactComponent as InIcon } from 'imgs/in.svg';
import {DocumentRenderer} from "@keystone-6/document-renderer";
import {reachGoal} from "../../lib/metrika";

const SELECTION = `
  id
  title
  items(orderBy: { order: asc }) {
    id
    name
    role
    linkedin
    description { document }
    photo { url }
    order
  }
`;

type SectionData = {
    id: string;
    title?: string | null;
    description?: string | null;
    footnote?: string | null;
    items: Item[];
};

type Item = {
    id: string;
    name: string;
    role: string;
    linkedin: string | null;
    description: { document?: any[] | null } | null;
    photo: { url?: string | null } | null;
    order: number;
};

const isLinkedInUrl = (url?: string): boolean => {
    if (!url) return false;

    return /^https?:\/\/(www\.)?linkedin\.com\/.+/i.test(url);
};

const LeadershipSection: React.FC<any> = ({}) => {
    const [open, setOpen] = useState<Item | null>(null);
    const {data: section, loading, error} = useSingleton<SectionData>(
        "LeadershipSection",
        SELECTION
    );

    // закрытие по Esc + запрет прокрутки под модалкой
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <Section id="leadership">
            <AsyncReveal loading={loading} error={error} stagger={0.08} from={12}>
                <Header>
                    <Title>{section?.title}</Title>
                </Header>

                <Grid>
                    {section?.items?.map((p) => (
                        <Card key={p.id} onClick={() => setOpen(p)} role="button" tabIndex={0}
                              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen(p)}>
                            {p.photo?.url
                                ? (
                                    <Portrait
                                        src={p.photo.url}
                                        alt={p.name}
                                        width={124}
                                        height={124}
                                    />
                                ) : (
                                    <PortraitPlaceholder/>
                                )}

                            <Name as="h3">{p.name}</Name>
                            <Role>{p.role}</Role>
                        </Card>
                    ))}
                </Grid>
            </AsyncReveal>

            {open && (
                <Modal onClick={() => setOpen(null)}>
                    <Dialog role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <CloseBtn aria-label="Close" onClick={() => setOpen(null)}>
                            <img src={closeIcon} alt="" width={32} height={32}/>
                        </CloseBtn>


                        <PopupBody>
                            {open.photo?.url
                                ? (
                                    <PopupPortrait
                                        src={open.photo?.url || ''}
                                        alt={open.name}
                                    />
                                ) : (
                                    <PopupPortraitPlaceholder/>
                                )}

                            <PopupHeader>
                                <PopupTitleRow>
                                    <PopupName>{open.name}</PopupName>
                                    {
                                        open.linkedin && isLinkedInUrl(open.linkedin) && (
                                            <LinkedinLink
                                              href={open.linkedin}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              aria-label={`LinkedIn profile of ${open.name}`}
                                              onClick={() => {
                                                  reachGoal("linkedin_click", {
                                                      place: "leadership_modal",
                                                      item_id: open.id,
                                                      url: open.linkedin ?? ''
                                                  });
                                              }}
                                            >
                                              <LinkedinIcon />
                                            </LinkedinLink>
                                        )
                                    }
                                </PopupTitleRow>
                                <PopupRole>{open.role}</PopupRole>
                            </PopupHeader>
                            <Desc>
                                <DocumentRenderer
                                    document={open?.description?.document || []}
                                />
                            </Desc>
                        </PopupBody>
                    </Dialog>
                </Modal>
            )}
        </Section>
    );
};

export default LeadershipSection;

/* ================= styles ================= */

const Section = styled.section`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 64px 10px;

    @media (max-width: 768px) {
        padding: 32px 10px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 32px;
`;

const Title = styled(Text40W700)`
    /* если подключен GT America LCG — заиспользуется */
    font-family: "GT America LCG", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
    text-align: center;
`;

const Grid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 32px; /* расстояние между карточками */
    justify-content: center; /* центрирует и полные, и неполные строки */
`;

const Card = styled.button`
    /* Десктоп: фиксированная карточка, чтобы красиво центровалась последняя строка */
    flex: 0 0 240px; /* ширина карточки 240px */
    max-width: 240px;

    background: transparent;
    border: none;
    padding: 0;
    text-align: center;
    cursor: pointer;

    display: flex;
    flex-direction: column;
    align-items: center;

    &:focus-visible {
        outline: 2px solid #16a34a;
        outline-offset: 4px;
    }

    /* Мобилка: две колонки на процентах с учётом gap */
    @media (max-width: 768px) {
        flex: 0 1 calc((100% - 32px) / 2); /* 2 колонки с 32px зазором */
        max-width: calc((100% - 32px) / 2);
    }
`;


const Portrait = styled.img`
    width: 124px;
    height: 124px;
    border-radius: 16px;
    object-fit: cover;
    object-position: center top;
    background-color: #D9D9D9;
`;

const PortraitPlaceholder = styled.div`
    width: 124px;
    height: 124px;
    background-color: #D9D9D9;
    border-radius: 16px;
`;

const Name = styled(Text24W700)`
    margin-top: 32px; /* отступ от фото до ФИО — 32 */
`;

const Role = styled(Text18W400)`
    margin-top: 6px;

    @media (max-width: 768px) {
        max-width: 290px;
    }
`;

/* ---------- modal ---------- */

const Modal = styled.div`
    position: fixed;
    overflow: auto;
    inset: 0;
    background: rgba(29, 32, 35, 0.45);
    display: grid;
    place-items: center;
    padding: 16px;
    z-index: 1000;
`;

const Dialog = styled.div`
    position: relative;
    background: #ffffff;
    border-radius: 16px; /* радиус попапа 16 */
    width: min(1024px, 100%);
    padding: 56px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
        padding: 24px;
    }
`;

const CloseBtn = styled.button`
    position: absolute;
    right: 20px;
    top: 20px;
    width: 56px;
    height: 56px; /* размер кнопки 56×56 */
    border-radius: 50%;
    background: #f6f6f6; /* фон кнопки закрытия */
    border: none;
    display: grid;
    place-items: center;
    cursor: pointer;

    img {
        width: 32px;
        height: 32px;
    }

    &:hover {
        background: #D9D9D9;
        transition: background ease-in-out 0.3s;
    }

    /* иконка 32×32 */
`;

const PopupHeader = styled.div`
    grid-area: header;
    margin-right: 72px; /* чтобы кнопка закрытия не перекрывала заголовок */
`;

const PopupTitleRow = styled.div`
    margin-bottom: 20px;
`;

const PopupName = styled(Text40W700)`
    display: inline;  
    white-space: normal;
    overflow-wrap: anywhere;
    margin-right: 16px;

    @media (max-width: 768px) {
      margin-right: 12px;
    }
`;

const LinkedinLink = styled.a`
    display: inline-block;
    margin-top: 4px;
    transition: opacity 0.15s ease;
    
    &:hover {
      opacity: 0.85;
    }
`

const LinkedinIcon = styled(InIcon)`
    width: 30px;
    height: 30px;
  
    path {
      fill: #3171AD;
    }
  
    @media (max-width: 768px) {
      width: 24px;
      height: 24px;
    }
`;

const PopupRole = styled(Text18W400)`
    color: #24303B;
`;

const PopupBody = styled.div`
    display: grid;
    grid-template-columns: 254px 1fr;
    grid-template-rows: auto 1fr;
    gap: 20px 48px;

    grid-template-areas:
    "portrait header"
    "portrait desc";

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        grid-template-rows: auto auto 1fr;
        gap: 20px;

        grid-template-areas:
      "header"
      "portrait"
      "desc";
    }
`;

const PopupPortrait = styled.img`
    grid-area: portrait;
    width: min(254px, 100%);
    aspect-ratio: 1 / 1;
    border-radius: 16px;
    object-fit: cover;
    object-position: center top;
    background-color: #D9D9D9;
`;

const PopupPortraitPlaceholder = styled.div`
    grid-area: portrait;
    width: min(254px, 100%);
    aspect-ratio: 1 / 1;
    background-color: #D9D9D9;
    border-radius: 16px;
`;

const Desc = styled(Text18W400)`
    grid-area: desc;
    color: #24303B;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;
